import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, Container } from "reactstrap";
import { Spin } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import editIcon from "assets/images/svg/Pencil Drawing.svg";
import removeIcon from "assets/images/svg/Delete.svg";
import NewFaultyReasonModal from "pages/meter-management/faulty-reasons/NewFaultyReasonModal";
import { DictionariesSelector } from "store/dictionaries/selectors";
import { getDictionary } from "store/dictionaries/actions";
import { DICTIONARY_TYPES } from "store/dictionaries/constants";
import { createDictionary } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import { getFormattedKeyName } from "utils/helpers/functions";
import NGIPrompt from "components/Common/NGIPrompt";
import { USER_ROLES } from "utils/constants";
import { formatReasons, prepareReasons } from "pages/meter-management/faulty-reasons/utils";
import classnames from "classnames";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  builderContainer: {
    width: "50%",

    "@media (max-width: 780px)": {
      width: "100%",
    },
  },
  faultyReasonItem: {
    display: "flex",
    justifyContent: "space-between",
    background: "#FFFFFF",
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.08)",
    borderRadius: 5,
    padding: 20,
    marginTop: 30,
    marginBottom: 15,
  },
  subItemContainer: {
    paddingLeft: 40,
    marginLeft: 15,
    position: "relative",

    "&:before": {
      content: "''",
      width: 1,
      height: "100%",
      position: "absolute",
      top: 0,
      left: 6,
      backgroundColor: "rgba(0, 0, 0, 0.06)",
    },
    "&:last-child:before": {
      height: 46,
    },

    "&:after": {
      content: "''",
      width: 20,
      height: 1,
      position: "absolute",
      top: 45,
      left: 7,
      backgroundColor: "rgba(0, 0, 0, 0.06)",
    },
  },
  faultyReasonSubItem: {
    paddingTop: 15,
  },
  subItemTitle: {
    display: "flex",
    justifyContent: "space-between",
    background: "#FFFFFF",
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.08)",
    borderRadius: 5,
    padding: 20,
  },
  imgBtn: {
    cursor: "pointer",
    marginLeft: 10,
  },
  removeIcon: {
    width: 20,
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  noReasons: {
    marginTop: 20,
  },
  requiredFields: {
    opacity: 0.5,
    fontSize: 12,
  },
  meterTypes: {
    fontSize: 12,
    fontWeight: 700,
  },
  // buttons: {
  //   width: 80,
  // },
});

const TestingFaultyReasons = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isLoading, setLoader] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentFaultyReason, setCurrentFaultyReason] = useState("");
  const [faultyReasons, setFaultyReasons] = useState([]);
  const [allFaultyReasons, setAllFaultyReasons] = useState([]);
  const [isSub, setIsSub] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [removeItem, setRemoveItem] = useState(null);

  const {
    list: { meterFaultReasonsTesting },
  } = useSelector(DictionariesSelector);

  useEffect(() => {
    dispatch(getDictionary(DICTIONARY_TYPES.METER_FAULTY_REASONS_TESTING));
    dispatch(getDictionary(DICTIONARY_TYPES.METER_REPAIR_INPUT_FIELDS));
    dispatch(getDictionary(DICTIONARY_TYPES.METER_TYPE));
  }, []);

  useEffect(() => {
    if (meterFaultReasonsTesting?.length) {
      setFaultyReasons(prepareReasons(meterFaultReasonsTesting));
    }
  }, [meterFaultReasonsTesting]);

  const editFaultyReason = useCallback(
    event => {
      setCurrentFaultyReason(event.currentTarget.dataset.faultyReason);
      setAllFaultyReasons(faultyReasons);
      setIsOpen(true);
    },
    [faultyReasons]
  );

  const editSubFaultyReason = useCallback(
    event => {
      setCurrentFaultyReason(event.currentTarget.dataset.faultyReason);
      const [level1] = event.currentTarget.dataset.faultyReason.split(">");
      const l1Index = faultyReasons.findIndex(item => item.item === level1);
      setAllFaultyReasons(faultyReasons[l1Index].items);
      setIsSub(true);
      setIsOpen(true);
    },
    [faultyReasons]
  );

  const addSubFaultyReason = useCallback(
    event => {
      const [level1] = event.currentTarget.dataset.faultyReason.split(">");
      const l1Index = faultyReasons.findIndex(item => item.item === level1);
      setAllFaultyReasons(faultyReasons[l1Index].items || []);
      setIsSub(true);
      setCurrentFaultyReason(event.currentTarget.dataset.faultyReason);
      setIsOpen(true);
    },
    [faultyReasons]
  );

  const removeReason = useCallback(event => setRemoveItem(event.currentTarget.dataset.faultyReason), []);
  const closeConfirmCloseModal = useCallback(() => setRemoveItem(null), []);
  const confirmRemove = useCallback(() => {
    setFaultyReasons(prevState => prevState.filter(reason => reason.item !== removeItem));
    setDisabled(false);
  }, [removeItem]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsSub(false);
    setAllFaultyReasons([]);
    setCurrentFaultyReason("");
  }, []);

  const handleAddNew = useCallback(() => {
    setAllFaultyReasons(faultyReasons);
    setIsOpen(true);
  }, [faultyReasons]);

  const handleSaveReasons = useCallback(async () => {
    const formattedData = {
      name: DICTIONARY_TYPES.METER_FAULTY_REASONS_TESTING,
      isMultiLevel: true,
      items: formatReasons(faultyReasons),
    };
    setLoader(true);
    await createDictionary(formattedData)
      .then(() => {
        setLoader(false);
        successMessage("Faulty reasons saved successfully");
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [faultyReasons]);

  const handleSave = useCallback(
    (newFaultyReason, requiredRepairFields = [], meterTypes = [], isPhotoRequired) => {
      setDisabled(false);
      const [level1, level2] = newFaultyReason.split(">");
      const [oldLevel1, oldLevel2] = currentFaultyReason.split(">");

      setFaultyReasons(prevState => {
        const state = [...prevState];
        const l1Index = state.findIndex(item => item.item === oldLevel1);
        if (level2) {
          const l2Index = state[l1Index]?.items?.findIndex(item => item.item === oldLevel2);
          if (l2Index !== -1) {
            state[l1Index].items[l2Index].item = level2;
            state[l1Index].items[l2Index].requiredRepairFields = requiredRepairFields;
            state[l1Index].items[l2Index].meterTypes = meterTypes;
            state[l1Index].items[l2Index].isPhotoRequired = isPhotoRequired;
          } else {
            state[l1Index]?.items?.push({
              item: level2,
              requiredRepairFields,
              meterTypes,
              isPhotoRequired,
            });
          }
        } else {
          if (l1Index !== -1) {
            state[l1Index].item = level1;
            state[l1Index].requiredRepairFields = requiredRepairFields;
            state[l1Index].meterTypes = meterTypes;
            state[l1Index].isPhotoRequired = isPhotoRequired;
          } else {
            state.push({
              items: [],
              item: level1,
              requiredRepairFields,
              meterTypes,
              isPhotoRequired,
            });
          }
        }
        return state;
      });
      handleClose();
    },
    [currentFaultyReason]
  );

  return (
    <>
      <NGIPrompt show={!disabled} />
      <div className="page-content">
        <Container fluid>
          <Spin spinning={isLoading}>
            <>
              <div className={classes.root}>
                <div className={classes.cardBody}>
                  <div className={classes.buttonsContainer}>
                    <CustomButton
                      icon={<i className="bx bx-plus-circle" />}
                      type="primary"
                      text="NEW FAULTY REASON"
                      hideRoles={[
                        USER_ROLES.METER_ENGINEER,
                        USER_ROLES.PRODUCT_TECHNICIANS,
                        USER_ROLES.MWT,
                        USER_ROLES.PRODUCT_TECHNICIAN_LEAD,
                      ]}
                      onClick={handleAddNew}
                    />
                    <CustomButton
                      hideRoles={[USER_ROLES.MWT]}
                      type="primary"
                      text="Save"
                      disabled={disabled}
                      onClick={handleSaveReasons}
                    />
                  </div>
                  <div className={classes.builderContainer}>
                    {faultyReasons?.map(item => (
                      <div key={item.item}>
                        <div className={classes.faultyReasonItem}>
                          <div>
                            {item.item}
                            <span className={classes.requiredFields}>
                              {` ${
                                item?.requiredRepairFields?.length || item?.isPhotoRequired
                                  ? `(Required fields: ${[
                                      ...(item?.requiredRepairFields || []),
                                      item?.isPhotoRequired && "Photo",
                                    ]
                                      .filter(row => row)
                                      .map(getFormattedKeyName)
                                      .join(", ")})`
                                  : ""
                              }`}
                            </span>
                            <span className={classes.requiredFields}>
                              {(item?.requiredRepairFields?.length || item?.isPhotoRequired) && <br />}
                              {` ${
                                item?.meterTypes?.length
                                  ? `(Selected meter types: ${[...(item?.meterTypes || [])]
                                      .filter(row => row)
                                      .map(item => item)
                                      .join(", ")})`
                                  : ""
                              }`}
                            </span>
                          </div>
                          <div className={classes.buttons}>
                            <img
                              data-faulty-reason={item.item}
                              className={classes.imgBtn}
                              onClick={editFaultyReason}
                              src={editIcon}
                              alt="action-icon"
                            />
                            <img
                              data-faulty-reason={item.item}
                              className={classnames(classes.imgBtn, classes.removeIcon)}
                              onClick={removeReason}
                              src={removeIcon}
                              alt="action-icon"
                            />
                          </div>
                        </div>
                        {!!item?.items?.length &&
                          item?.items?.map(subItem => (
                            <div key={`${item.item}>${subItem.item}`} className={classes.subItemContainer}>
                              <div className={classes.faultyReasonSubItem}>
                                <div className={classes.subItemTitle}>
                                  <div>
                                    {subItem.item}
                                    <span className={classes.requiredFields}>
                                      {` ${
                                        subItem?.requiredRepairFields?.length || subItem?.isPhotoRequired
                                          ? `(Required fields: ${[
                                              ...(subItem?.requiredRepairFields || []),
                                              subItem?.isPhotoRequired && "Photo",
                                            ]
                                              .filter(row => row)
                                              .map(getFormattedKeyName)
                                              .join(", ")})`
                                          : ""
                                      }`}
                                    </span>
                                  </div>
                                  <div>
                                    <img
                                      data-faulty-reason={`${item.item}>${subItem.item}`}
                                      className={classes.imgBtn}
                                      onClick={editSubFaultyReason}
                                      src={editIcon}
                                      alt="action-icon"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ))}
                  </div>
                  {!faultyReasons.length && (
                    <Card className={classes.noReasons}>
                      <CardBody className="text-center">
                        <i className="bx bx-warning" />
                        <h5>No faulty reasons</h5>
                        <p className="text-muted">Please add new faulty reason</p>
                      </CardBody>
                    </Card>
                  )}
                </div>
              </div>
            </>
          </Spin>
        </Container>
        {!!removeItem && (
          <ConfirmationModal
            description={
              <span>
                Are you sure you want to remove <b>{removeItem}</b> reason?
              </span>
            }
            onCancel={closeConfirmCloseModal}
            onConfirm={confirmRemove}
          />
        )}
        {isOpen && (
          <NewFaultyReasonModal
            onClose={handleClose}
            faultyReason={currentFaultyReason}
            onSave={handleSave}
            isSub={isSub}
            allFaultyReasons={allFaultyReasons}
          />
        )}
      </div>
    </>
  );
};

export default TestingFaultyReasons;
